import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useImpersonation } from "../../contexts/ImpersonationContext";
import { getUser } from "../../services/user-service";
import { IUser } from "../../types/user";

const ImpersonationBanner: React.FC = () => {
  const { impersonatedUserId, clearImpersonation } = useImpersonation();
  const [impersonatedUserDetails, setImpersonatedUserDetails] =
    React.useState<IUser>();
  const lastRequestedId = React.useRef<string | undefined>();

  React.useEffect(() => {
    if (!impersonatedUserId) {
      setImpersonatedUserDetails(undefined);
      return;
    }

    // Avoid duplicate fetches for same id
    if (
      lastRequestedId.current === impersonatedUserId &&
      impersonatedUserDetails
    )
      return;

    let isActive = true;
    lastRequestedId.current = impersonatedUserId;

    getUser(impersonatedUserId)
      .then((res) => {
        if (isActive) setImpersonatedUserDetails(res);
      })
      .catch(() => {
        if (isActive) setImpersonatedUserDetails(undefined);
      });

    return () => {
      isActive = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonatedUserId]); // fetch only when the id changes

  if (!impersonatedUserId) return null;

  const handleExit = () => {
    setImpersonatedUserDetails(undefined);
    clearImpersonation();
    window.location.reload();
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFF6CC",
        borderBottom: "1px solid #FFD700",
        padding: "12px 16px",
        textAlign: "center",
        zIndex: 1200,
        position: "sticky",
        top: 0,
      }}
    >
      <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
        You are impersonating{" "}
        <strong>
          {impersonatedUserDetails
            ? `${impersonatedUserDetails?.firstName} ${impersonatedUserDetails?.lastName} 
          - ${impersonatedUserDetails?.org && impersonatedUserDetails?.org?.name} 
          - ${impersonatedUserDetails?.emailId}`
            : "Unknown user"}
        </strong>
      </Typography>
      <Button
        onClick={handleExit}
        size="small"
        sx={{ marginLeft: 3, fontWeight: 600 }}
        variant="outlined"
        color="warning"
      >
        Exit impersonation
      </Button>
    </Box>
  );
};

export default ImpersonationBanner;
